import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "@/api/client";
import type { components } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { VERDICT_TONE, errorMessage, orNull } from "@/features/think-tank/ttModel";

type TtPilot = components["schemas"]["TtPilotPublic"];
type TtPilots = components["schemas"]["TtPilots"];
type Opportunity = components["schemas"]["TtOpportunityName"];

const KEY = ["admin", "think-tank"] as const;

/** Pilots: every move starts small with a clear goal; wins become features, misses become lessons. */
export function Pilots() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: [...KEY, "pilots"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/think-tank/pilots");
      if (error) throw new Error("failed to load");
      return data!;
    },
  });
  const opps = useQuery({
    queryKey: [...KEY, "rules"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/think-tank/rules");
      if (error) throw new Error("failed to load");
      return data!;
    },
  });
  const refresh = () => qc.invalidateQueries({ queryKey: [...KEY] });
  const p: TtPilots | undefined = query.data;
  if (query.isLoading) return <p className="text-sm text-zinc-500">Loading…</p>;
  if (query.isError || !p) {
    return (
      <Card><CardBody>
        <p role="alert" className="text-sm text-red-700">Couldn’t load Pilots. Your session may have expired.</p>
        <Button size="sm" variant="secondary" className="mt-3" onClick={() => void query.refetch()}>Retry</Button>
      </CardBody></Card>
    );
  }
  const opportunities = opps.data?.opportunities ?? [];
  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-3">
        {([["running", "Running", p.running], ["proposed", "Proposed", p.proposed], ["finished", "Finished", p.finished]] as const).map(([key, label, list]) => (
          <div key={key} className="space-y-3" data-testid={`tt-pilots-${key}`}>
            <h2 className="text-sm font-semibold uppercase text-zinc-500">{`${label} · ${list.length}`}</h2>
            {list.map(x => <Pilot key={x.id} p={x} opportunities={opportunities} onChanged={refresh} />)}
            {!list.length ? <p className="text-sm text-zinc-500">None.</p> : null}
          </div>
        ))}
      </div>
      <Propose opportunities={opportunities} onChanged={refresh} />
      <Card><CardBody>
        <p className="text-sm"><span className="font-semibold">The loop: </span>{p.loop_line}</p>
        <p className="mt-1 text-xs text-zinc-500">{p.bar_line}</p>
      </CardBody></Card>
    </div>
  );
}

function Pilot({ p, opportunities, onChanged }: { p: TtPilot; opportunities: Opportunity[]; onChanged: () => void }) {
  const [changing, setChanging] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [actual, setActual] = useState(String(p.actual));
  const [shareYes, setShareYes] = useState(String(p.share_yes ?? ""));
  const [note, setNote] = useState("");
  const call = useMutation({
    mutationFn: async (fn: () => Promise<{ error?: unknown }>) => {
      const { error } = await fn();
      if (error) throw new Error(errorMessage(error, "Couldn’t save that."));
    },
    onSuccess: () => { setChanging(false); setFinishing(false); onChanged(); },
  });
  const path = { params: { path: { pilot_id: p.id } } };
  // The numbers on screen, saved together with finishing (TT review fixes 2).
  const shown = { actual: Number(actual) || 0, ...(p.share_label ? { share_yes: shareYes === "" ? null : Number(shareYes) } : {}) };
  return (
    <Card data-testid={`tt-pilot-${p.id}`}><CardBody>
      <p className="text-xs text-zinc-500">{p.sample ? `Sample · ${p.status_line}` : p.status_line}</p>
      <h3 className="mt-1 font-semibold">{p.title}</h3>
      {p.idea ? <p className="mt-1 text-sm text-zinc-700">{p.idea}</p> : null}
      <p className="mt-1 text-sm">{`Goal: ${p.goal}`}</p>
      {p.status !== "proposed" ? <p className={`mt-1 text-sm ${p.verdict ? VERDICT_TONE[p.verdict] : "text-zinc-700"}`} data-testid={`tt-progress-${p.id}`}>{p.progress_line}</p> : null}
      {p.outcome_line ? <p className="mt-1 text-sm font-medium" data-testid={`tt-outcome-${p.id}`}>{p.outcome_line}</p> : null}
      {p.opportunity_title ? <p className="mt-1 text-xs text-zinc-500">{`For: ${p.opportunity_title}`}</p> : null}
      {p.status === "proposed" ? (
        <div className="mt-2 flex gap-2">
          <Button size="sm" disabled={call.isPending} data-testid={`tt-approve-${p.id}`}
            onClick={() => call.mutate(() => api.POST("/api/v1/admin/think-tank/pilots/{pilot_id}/approve", path))}>Approve</Button>
          <Button size="sm" variant="secondary" data-testid={`tt-change-${p.id}`} onClick={() => setChanging(!changing)}>Change</Button>
        </div>
      ) : null}
      {changing ? <PilotForm p={p} opportunities={opportunities} onDone={onChanged} /> : null}
      {p.status === "running" ? (
        <div className="mt-2 space-y-2" role="group" aria-label="Update the numbers">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <label className="flex items-center gap-1">{p.metric}
              <input aria-label={`How many ${p.metric}`} type="number" min={0} value={actual} onChange={e => setActual(e.target.value)}
                className="h-8 w-20 rounded-md border border-zinc-300 px-2" data-testid={`tt-actual-${p.id}`} />
            </label>
            {p.share_label ? (
              <label className="flex items-center gap-1">{p.share_label}
                <input aria-label={`How many ${p.share_label}`} type="number" min={0} value={shareYes} onChange={e => setShareYes(e.target.value)}
                  className="h-8 w-20 rounded-md border border-zinc-300 px-2" data-testid={`tt-share-${p.id}`} />
              </label>
            ) : null}
            <Button size="sm" variant="secondary" disabled={call.isPending} data-testid={`tt-numbers-${p.id}`}
              onClick={() => call.mutate(() => api.PATCH("/api/v1/admin/think-tank/pilots/{pilot_id}", { ...path,
                body: { actual: Number(actual) || 0, ...(p.share_label ? { share_yes: shareYes === "" ? null : Number(shareYes) } : {}) } }))}>Save numbers</Button>
            <Button size="sm" variant="ghost" data-testid={`tt-finish-${p.id}`} onClick={() => setFinishing(!finishing)}>Finish</Button>
          </div>
          {finishing ? (
            <div className="space-y-2 rounded-md border border-zinc-300 bg-zinc-50 p-2" role="group" aria-label="Finish the pilot">
              <p className="text-sm">{`Finish it with ${Number(actual) || 0} ${p.metric}${p.share_label ? ` and ${shareYes === "" ? 0 : Number(shareYes)} ${p.share_label}` : ""}? The result is kept as it is.`}</p>
              <input aria-label="What we learned" placeholder="What we learned (optional)" value={note} onChange={e => setNote(e.target.value)}
                className="h-8 w-full rounded-md border border-zinc-300 px-2 text-sm" />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" disabled={call.isPending} data-testid={`tt-finish-feature-${p.id}`}
                  onClick={() => call.mutate(() => api.POST("/api/v1/admin/think-tank/pilots/{pilot_id}/finish", { ...path, body: { outcome: "feature", note: orNull(note), ...shown } }))}>It becomes a feature</Button>
                <Button size="sm" variant="secondary" disabled={call.isPending} data-testid={`tt-finish-lesson-${p.id}`}
                  onClick={() => call.mutate(() => api.POST("/api/v1/admin/think-tank/pilots/{pilot_id}/finish", { ...path, body: { outcome: "lesson", note: orNull(note), ...shown } }))}>Learn and move on</Button>
                <Button size="sm" variant="ghost" onClick={() => setFinishing(false)}>Not yet</Button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      {call.isError ? <p role="alert" className="mt-1 text-sm text-red-700">{call.error.message}</p> : null}
    </CardBody></Card>
  );
}

type Fields = { title: string; opportunity_id: string; idea: string; goal: string; metric: string; target: string; share_label: string; share_goal: string; days: string };
const empty: Fields = { title: "", opportunity_id: "", idea: "", goal: "", metric: "", target: "", share_label: "", share_goal: "", days: "60" };

function body(f: Fields) {
  return {
    title: f.title.trim(), opportunity_id: f.opportunity_id || null, idea: orNull(f.idea), goal: f.goal.trim(), metric: f.metric.trim(),
    target: Number(f.target), share_label: orNull(f.share_label), share_goal: f.share_label.trim() ? Number(f.share_goal) || null : null, days: Number(f.days) || 60,
  };
}

function PilotForm({ p, opportunities, onDone }: { p?: TtPilot; opportunities: Opportunity[]; onDone: () => void }) {
  const [f, setF] = useState<Fields>(p ? {
    title: p.title, opportunity_id: p.opportunity_id ?? "", idea: p.idea ?? "", goal: p.goal, metric: p.metric, target: String(p.target),
    share_label: p.share_label ?? "", share_goal: p.share_goal ? String(p.share_goal) : "", days: String(p.days),
  } : empty);
  const set = (k: keyof Fields) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const save = useMutation({
    mutationFn: async () => {
      const { error } = p
        ? await api.PATCH("/api/v1/admin/think-tank/pilots/{pilot_id}", { params: { path: { pilot_id: p.id } }, body: body(f) })
        : await api.POST("/api/v1/admin/think-tank/pilots", { body: body(f) });
      if (error) throw new Error(errorMessage(error, "Couldn’t save the pilot."));
    },
    onSuccess: () => { if (!p) setF(empty); onDone(); },
  });
  const ok = f.title.trim() && f.goal.trim().length >= 3 && f.metric.trim() && Number(f.target) >= 1;
  const cls = "h-9 rounded-md border border-zinc-300 px-2 text-sm";
  return (
    <div className="mt-2 grid gap-2 sm:grid-cols-2" role="group" aria-label={p ? "Change the proposal" : "Propose a pilot"}>
      <input aria-label="Pilot name" placeholder="Pilot name" value={f.title} onChange={set("title")} className={cls} data-testid={p ? "tt-pf-title-edit" : "tt-pf-title"} />
      <select aria-label="For which opportunity" value={f.opportunity_id} onChange={set("opportunity_id")} className={cls}>
        <option value="">For which opportunity (optional)</option>
        {opportunities.map(o => <option key={o.id} value={o.id}>{o.title}</option>)}
      </select>
      <input aria-label="What we'd try" placeholder="What we’d try (optional)" value={f.idea} onChange={set("idea")} className={`${cls} sm:col-span-2`} />
      <input aria-label="Goal" placeholder="Goal: what has to happen, by when" value={f.goal} onChange={set("goal")} className={`${cls} sm:col-span-2`} data-testid={p ? "tt-pf-goal-edit" : "tt-pf-goal"} />
      <input aria-label="What we count" placeholder="What we count" value={f.metric} onChange={set("metric")} className={cls} data-testid={p ? "tt-pf-metric-edit" : "tt-pf-metric"} />
      <input aria-label="How many" placeholder="How many" type="number" min={1} value={f.target} onChange={set("target")} className={cls} data-testid={p ? "tt-pf-target-edit" : "tt-pf-target"} />
      <input aria-label="A second measure" placeholder="A second measure to count (optional)" value={f.share_label} onChange={set("share_label")} className={cls} />
      <input aria-label="Its goal, in percent" placeholder="Its goal, %" type="number" min={1} max={100} value={f.share_goal} onChange={set("share_goal")} className={cls} disabled={!f.share_label.trim()} />
      <label className="flex items-center gap-2 text-sm text-zinc-600">How many days
        <input aria-label="Days" placeholder="Days" type="number" min={1} max={365} value={f.days} onChange={set("days")} className={`${cls} w-24`} />
      </label>
      <Button size="sm" className="sm:w-fit" disabled={!ok || save.isPending} data-testid={p ? "tt-pf-save-edit" : "tt-pf-save"} onClick={() => save.mutate()}>{p ? "Save changes" : "Propose"}</Button>
      {save.isError ? <p role="alert" className="text-sm text-red-700 sm:col-span-2">{save.error.message}</p> : null}
    </div>
  );
}

function Propose({ opportunities, onChanged }: { opportunities: Opportunity[]; onChanged: () => void }) {
  return (
    <Card data-testid="tt-propose"><CardBody>
      <h2 className="font-semibold">Propose a pilot</h2>
      <p className="text-sm text-zinc-500">Small, with one clear goal. Approving it starts the clock.</p>
      <PilotForm opportunities={opportunities} onDone={onChanged} />
    </CardBody></Card>
  );
}
